import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store';
import { OrderCardErrorReason } from '../../../model';
import { CurrenciesActionTypes } from '../../../site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { OrderCardsConnector } from '../../connectors/order-cards-connector';
import { UserActions } from '../actions';

@Injectable()
export class OrderCardsEffects {
  private contextChange$ = this.actions$.pipe(
    ofType(CurrenciesActionTypes.CurrencyChange, LanguagesActionTypes.LanguageChange, LoginLogoutActionTypes.Logout)
  );
  private logoutOnlyContextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadOrderCard$: Observable<UserActions.LoadOrderCardSuccess | UserActions.LoadOrderCardFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsActionTypes.LoadOrderCard),
      switchMap((action: UserActions.LoadOrderCard) =>
        this.connector.getOrderCard(action.userId, action.code).pipe(
          map((data) => new UserActions.LoadOrderCardSuccess(action.code, data)),
          catchError((error) => of(new UserActions.LoadOrderCardFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  getOrderCards$: Observable<UserActions.SearchOrderCardsSuccess | UserActions.SearchOrderCardsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsActionTypes.SearchOrderCards),
      map((action: UserActions.SearchOrderCards) => action.payload),
      switchMap((payload) =>
        this.connector.getOrderCards(payload.userId, payload.searchParams).pipe(
          map((data) => new UserActions.SearchOrderCardsSuccess({ ...payload, searchResult: data })),
          catchError((error) =>
            of(
              new UserActions.SearchOrderCardsFail({
                ...payload,
                error: normalizeHttpError(error),
              })
            )
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadOrderCardConfigurations$: Observable<
    | UserActions.LoadOrderCardConfigurationsSuccess
    | UserActions.LoadOrderCardConfigurationsFail
    | UserActions.LoadOrderCardConfigurationSuccess
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsActionTypes.LoadOrderCardConfigurations),
      mergeMap((action: UserActions.LoadOrderCardConfigurations) =>
        this.connector.getOrderCardConfigurations(action.userId, action.code).pipe(
          switchMap((data) => [
            new UserActions.LoadOrderCardConfigurationsSuccess(action.code, data),
            ...data.map((config) => new UserActions.LoadOrderCardConfigurationSuccess(action.code, config.product, config)),
          ]),
          catchError((error) => of(new UserActions.LoadOrderCardConfigurationsFail(action.code, normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  loadOrderCardConfiguration$: Observable<
    UserActions.LoadOrderCardConfigurationSuccess | UserActions.LoadOrderCardConfigurationFail
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsActionTypes.LoadOrderCardConfiguration),
      mergeMap((action: UserActions.LoadOrderCardConfiguration) =>
        this.connector.getOrderCardConfiguration(action.userId, action.code, action.productCode).pipe(
          map((data) => new UserActions.LoadOrderCardConfigurationSuccess(action.code, action.productCode, data)),
          catchError((error) =>
            of(new UserActions.LoadOrderCardConfigurationFail(action.code, action.productCode, normalizeHttpError(error)))
          )
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  updateOrderCardConfiguration$: Observable<
    UserActions.UpdateOrderCardConfigurationSuccess | UserActions.UpdateOrderCardConfigurationFail | UserActions.LoadOrderCard
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.OrderCardsActionTypes.UpdateOrderCardConfiguration),
      switchMap((action: UserActions.UpdateOrderCardConfiguration) =>
        this.connector.updateOrderCardConfiguration(action.userId, action.code, action.params).pipe(
          map((data) => new UserActions.UpdateOrderCardConfigurationSuccess(action.code, action.productCode, data)),
          catchError((error) => {
            const normalizedError = normalizeHttpError(error);
            return normalizedError?.details?.[0]?.reason === OrderCardErrorReason.ORDER_CARD_CONFIGURATION_UPDATE_FAILED
              ? of(
                  new UserActions.UpdateOrderCardConfigurationFail(action.code, action.productCode, normalizedError),
                  new UserActions.LoadOrderCard(action.code, action.userId)
                )
              : of(new UserActions.UpdateOrderCardConfigurationFail(action.code, action.productCode, normalizedError));
          })
        )
      ),
      withdrawOn(this.logoutOnlyContextChange$)
    )
  );

  handleFailureForUpdateOrderCardConfiguration$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> =
    createEffect(() =>
      this.actions$.pipe(
        ofType(UserActions.OrderCardsActionTypes.UpdateOrderCardConfigurationFail),
        map((action: UserActions.UpdateOrderCardConfigurationFail) => {
          if (!isErrorGloballyHandled(action.payload.error?.status)) {
            return new GlobalMessageActions.AddMessage({
              text: {
                key: 'orderCards.updateOrderCardConfigurationFailure_message',
              },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            });
          } else {
            return new AppActions.NoOpAction();
          }
        })
      )
    );

  constructor(private actions$: Actions, private connector: OrderCardsConnector) {}
}
